import Papa from "papaparse";
import Config from "../config";
import { FilterValue } from "antd/es/table/interface";
import { ProgramId } from "src/models/BaseModel";

/**
 * Return ellipsis of a given string
 * @param {string} text
 * @param {number} size
 */
export const ellipsis = (text: string, size: number) => {
  return text.length > size ? `${text.slice(0, size)}...` : text;
};

export const keyValArrayToObject = (array: { key: string; value: any }[]) => {
  return array.reduce(
    (obj, item) => {
      obj[item.key] = item.value;
      return obj;
    },
    {} as { [key: string]: any },
  );
};

export const formatNumber = (number: number) => {
  return number.toLocaleString("en-US");
};

export const compactNumber = (value: number) => {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
  }).format(value);
};

export const normalizeBy = <T extends Record<string, any>>(
  array: T[],
  key: string | number,
) => {
  return array.reduce(
    (obj, item) => {
      obj[item[key]] = item;
      return obj;
    },
    {} as { [key: string | number]: T },
  );
};

export const exportToCSV = <T>(data: T[], filename: string): void => {
  const csv = Papa.unparse(data);

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

export const capitalize = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const constructFilterString = (
  filters: Record<string, FilterValue | null>,
  curProgram?: ProgramId | null,
): string => {
  const filterParts = Object.keys(filters)
    .map((key) => {
      const filterValue = filters[key];
      if (!filterValue || filterValue.length === 0) {
        return null;
      }

      const value = (filterValue as string[])[0];

      if (key === "dateCreated" || key === "creditDate") {
        const [startDate, endDate] = value.split("<>");

        if (startDate && endDate) {
          return `${key} >= "${startDate}" && ${key} <= "${endDate}"`;
        }
        return null;
      }

      if (key === "transactionStatusId") {
        return `${key} == ${value}`;
      }

      if (key === "redemptionErrorCode") {
        const [filterVal, operator] = value.split("<>");

        if (filterVal) {
          const op = operator || "==";
          return `${key} ${op} ${filterVal}`;
        }
        return null;
      }

      if (key === "projectValue") {
        return `${key} in [${value.split(",").map((v) => `"${v}"`)}]`;
      }

      if (curProgram !== "0") {
        if (key === "customerId") return `username contains ${value}`;

        if (key === "siebelOrderId") return `requestId contains ${value}`;

        if (key === "redeemedPoints") return `points contains ${value}`;
      }

      return `${key} contains ${value}`;
    })
    .filter(Boolean);

  return filterParts.join(" && ");
};

export const isProgramId = (id: string | null | undefined): id is ProgramId => {
  return id !== null && id !== undefined && id in Config;
};
